import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useUser } from '../../context/UserContext';
import Button from '../common/Button';
import Toast from '../common/Toast';
import logo from '../../assets/images/logo.jpeg';

const AuthPage = () => {
    const navigate = useNavigate();
    const { user, login, signup } = useUser();
    const [isLogin, setIsLogin] = useState(true);

    const checkAdminRedirect = (userObj) => {
        if (!userObj) return null;
        const isAdmin =
            userObj.is_staff === true ||
            userObj.is_staff === 1 ||
            userObj.is_staff === "true" ||
            userObj.is_superuser === true ||
            userObj.is_superuser === 1 ||
            userObj.is_superuser === "true" ||
            userObj.role === 'admin' ||
            userObj.user_type === 'admin';

        console.log("Checking admin status for:", userObj.email, "Result:", isAdmin);
        return isAdmin ? '/admin' : '/';
    };

    useEffect(() => {
        if (user) {
            const path = checkAdminRedirect(user);
            if (path) navigate(path, { replace: true });
        }
    }, [user, navigate]);
    const [showPassword, setShowPassword] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (isLogin) {
                const result = await login(formData.email, formData.password);
                if (result.success) {
                    setToastMessage('Connexion réussie !');
                    setShowToast(true);
                    // Selection is handled by the useEffect above when 'user' state changes in Context
                } else {
                    setToastMessage(result.message);
                    setShowToast(true);
                }
            } else {
                if (formData.password !== formData.confirmPassword) {
                    setToastMessage('Les mots de passe ne correspondent pas');
                    setShowToast(true);
                    setIsSubmitting(false);
                    return;
                }

                // Construct registration data
                const registerData = {
                    full_name: formData.name,
                    email: formData.email,
                    password: formData.password,
                    confirmPassword: formData.confirmPassword
                };

                const result = await signup(registerData);
                if (result.success) {
                    setToastMessage('Compte créé avec succès !');
                    setShowToast(true);
                    // Selection is handled by the useEffect above when 'user' state changes in Context
                } else {
                    setToastMessage(result.message);
                    setShowToast(true);
                }
            }
        } catch (error) {
            setToastMessage('Une erreur inattendue est survenue');
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center py-20 px-6">
            <div className="w-full max-w-[1100px] grid grid-cols-1 lg:grid-cols-2 bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100">

                {/* Left Side: Visual/Context */}
                <div className="hidden lg:block relative bg-brand-dark p-16 text-white overflow-hidden">
                    <div className="relative z-10 h-full flex flex-col justify-between">
                        <div>
                            <Link to="/" className="inline-flex items-center gap-2 group mb-12">
                                <div className="h-32 w-auto min-w-[240px] overflow-hidden rounded-xl">
                                    <img src={logo} alt="SHOWROOM Logo" className="h-full w-auto object-contain transition-transform group-hover:scale-105" />
                                </div>
                            </Link>

                            <h2 className="text-4xl font-bold mb-6 leading-tight">
                                {isLogin ? "Ravi de vous revoir parmi nous." : "Commencez votre voyage avec nous."}
                            </h2>
                            <p className="text-lg text-white/70 leading-relaxed">
                                Accédez à des collections exclusives, suivez vos commandes et profitez d'une expérience de shopping personnalisée.
                            </p>
                        </div>

                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                                    <CheckCircle2 className="w-6 h-6 text-brand" />
                                </div>
                                <div>
                                    <p className="font-bold">Qualité Premium</p>
                                    <p className="text-sm text-white/60">Une sélection rigoureuse des meilleurs matériaux.</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                                    <CheckCircle2 className="w-6 h-6 text-brand" />
                                </div>
                                <div>
                                    <p className="font-bold">Service Client Dédié</p>
                                    <p className="text-sm text-white/60">Nous vous accompagnons à chaque étape.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Decorative Elements */}
                    <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-brand/20 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-brand/10 rounded-full blur-3xl"></div>
                </div>

                {/* Right Side: Form */}
                <div className="p-8 md:p-16 flex flex-col justify-center">
                    <div className="max-w-md mx-auto w-full">
                        <div className="mb-10 text-center lg:text-left">
                            <h1 className="text-3xl font-bold text-brand-dark mb-3">
                                {isLogin ? "Connexion" : "Créer un compte"}
                            </h1>
                            <p className="text-slate-500">
                                {isLogin ? "Entrez vos identifiants pour accéder à votre compte" : "Rejoignez notre communauté de passionnés de design"}
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            {!isLogin && (
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-brand-dark ml-1">Nom Complet</label>
                                    <div className="relative">
                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            required={!isLogin}
                                            placeholder="John Doe"
                                            className="w-full h-14 pl-12 pr-6 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-brand focus:bg-white transition-all outline-none"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-brand-dark ml-1">Adresse Email</label>
                                <div className="relative">
                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="votre@email.com"
                                        className="w-full h-14 pl-12 pr-6 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-brand focus:bg-white transition-all outline-none"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between px-1">
                                    <label className="text-sm font-bold text-brand-dark">Mot de passe</label>
                                    {isLogin && (
                                        <button type="button" className="text-xs font-bold text-brand hover:text-brand-dark">Mot de passe oublié ?</button>
                                    )}
                                </div>
                                <div className="relative">
                                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="••••••••"
                                        className="w-full h-14 pl-12 pr-12 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-brand focus:bg-white transition-all outline-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            {!isLogin && (
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-brand-dark ml-1">Confirmer le mot de passe</label>
                                    <div className="relative">
                                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="confirmPassword"
                                            value={formData.confirmPassword}
                                            onChange={handleInputChange}
                                            required={!isLogin}
                                            placeholder="••••••••"
                                            className="w-full h-14 pl-12 pr-6 rounded-2xl bg-slate-50 border-2 border-transparent focus:border-brand focus:bg-white transition-all outline-none"
                                        />
                                    </div>
                                </div>
                            )}

                            <Button
                                variant="primary"
                                type="submit"
                                className="w-full h-14 text-base shadow-xl shadow-brand-dark/10 group mt-4"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        Chargement...
                                    </div>
                                ) : (
                                    <>
                                        {isLogin ? "Se connecter" : "Créer mon compte"}
                                        <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                                    </>
                                )}
                            </Button>
                        </form>


                        <p className="mt-10 text-center text-slate-500 font-medium">
                            {isLogin ? "Vous n'avez pas de compte ?" : "Vous avez déjà un compte ?"}
                            <button
                                onClick={() => setIsLogin(!isLogin)}
                                className="ml-2 text-brand font-bold hover:text-brand-dark underline underline-offset-4"
                            >
                                {isLogin ? "Inscrivez-vous" : "Connectez-vous"}
                            </button>
                        </p>
                    </div>
                </div>
            </div>

            <Toast
                message={toastMessage}
                isOpen={showToast}
                onClose={() => setShowToast(false)}
            />
        </div >
    );
};

export default AuthPage;

